import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaBookOpen, 
  FaGraduationCap, 
  FaLaptopCode, 
  FaUsers, 
  FaChartLine, 
  FaAward,
  FaArrowRight,
  FaPlay,
  FaStar,
  FaCheckCircle,
  FaHeart,
  FaHandHoldingHeart,
  FaGlobeAfrica,
  FaChild,
  FaSchool,
  FaLightbulb,
  FaDonate,
  FaHandshake,
  FaBuilding,
  FaSeedling
} from 'react-icons/fa';

const LXDHome = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  const heroSlides = [
    {
      title: "100% Free Education for All Ethiopians",
      subtitle: "LXD Ethiopia NGO",
      description: "We provide completely free, accessible education to underserved communities across Ethiopia. No fees, no barriers - just opportunity for everyone.",
      gradient: "from-green-600 via-emerald-600 to-teal-500",
      icon: <FaHandHoldingHeart className="text-6xl" />
    },
    {
      title: "Free Digital Skills Training",
      subtitle: "No Cost, No Barriers",
      description: "Every course, every lesson, every resource is completely free. We believe education should be accessible to everyone regardless of their financial situation.",
      gradient: "from-blue-600 via-indigo-600 to-purple-500",
      icon: <FaChild className="text-6xl" />
    },
    {
      title: "Transforming Lives Through Free Education",
      subtitle: "Our NGO Mission",
      description: "Join thousands of Ethiopians who have already transformed their lives through our free educational programs. Your future starts here, at no cost to you.",
      gradient: "from-orange-500 via-red-500 to-pink-500",
      icon: <FaHeart className="text-6xl" />
    }
  ];

  const impactStories = [
    {
      id: 1,
      title: "Digital Learning Center in Hawassa",
      location: "Hawassa, Ethiopia",
      beneficiaries: 500,
      story: "We established our first digital learning center in Hawassa, providing free computer education to 500 students from low-income families.",
      image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=400",
      category: "Education"
    },
    {
      id: 2,
      title: "Rural School Computer Lab Project",
      location: "Bale Zone, Oromia",
      beneficiaries: 1200,
      story: "Our team installed 50 computers in rural schools, giving 1,200 children their first access to digital education.",
      image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400",
      category: "Infrastructure"
    },
    {
      id: 3,
      title: "Women's Digital Literacy Program",
      location: "Addis Ababa",
      beneficiaries: 800,
      story: "Empowering 800 women with digital skills to help them start businesses and improve their livelihoods.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400",
      category: "Empowerment"
    },
    {
      id: 4,
      title: "Youth Coding Bootcamp",
      location: "Dire Dawa",
      beneficiaries: 300,
      story: "Free coding bootcamp for 300 youth, with 85% securing tech jobs within 6 months of completion.",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400",
      category: "Youth Development"
    }
  ];

  const freeCourses = [
    {
      id: 1,
      title: "Digital Marketing Mastery",
      category: "Business",
      level: "Beginner",
      duration: "8 weeks",
      students: 2340,
      rating: 4.8,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400",
      instructor: "Dr. Abebe Kebede"
    },
    {
      id: 2,
      title: "Web Development Bootcamp",
      category: "Technology",
      level: "Intermediate",
      duration: "12 weeks",
      students: 1890,
      rating: 4.9,
      image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400",
      instructor: "Eng. Selamawit Bekele"
    },
    {
      id: 3,
      title: "Data Science with Python",
      category: "Data Science",
      level: "Advanced",
      duration: "16 weeks",
      students: 1450,
      rating: 4.7,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
      instructor: "Prof. Dawit Haile"
    },
    {
      id: 4,
      title: "Entrepreneurship in Ethiopia",
      category: "Business",
      level: "Beginner",
      duration: "6 weeks",
      students: 3200,
      rating: 4.9,
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400",
      instructor: "Mrs. Tigist Haile"
    }
  ];

  const slideshowImages = [
    {
      image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=1200",
      caption: "Students learning at our Hawassa Digital Center",
      location: "Hawassa, Ethiopia"
    },
    {
      image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=1200",
      caption: "Children using computers for the first time",
      location: "Bale Zone, Oromia"
    },
    {
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1200",
      caption: "Women's digital literacy program in action",
      location: "Addis Ababa"
    },
    {
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1200",
      caption: "Youth coding bootcamp graduates",
      location: "Dire Dawa"
    },
    {
      image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1200",
      caption: "Community education outreach program",
      location: "Tigray Region"
    },
    {
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200",
      caption: "Volunteers teaching digital skills",
      location: "Amhara Region"
    }
  ];

  const stats = [
    { number: "15,000+", label: "Lives Impacted", icon: <FaHeart /> },
    { number: "45+", label: "Communities Served", icon: <FaGlobeAfrica /> },
    { number: "120+", label: "Schools Supported", icon: <FaSchool /> },
    { number: "500+", label: "Volunteers", icon: <FaHandshake /> }
  ];

  const features = [
    {
      icon: <FaSeedling className="text-3xl" />,
      title: "Community Development",
      description: "We work directly with communities to understand their needs and provide tailored educational solutions."
    },
    {
      icon: <FaSchool className="text-3xl" />,
      title: "School Infrastructure",
      description: "Building and equipping computer labs in schools across Ethiopia to bridge the digital divide."
    },
    {
      icon: <FaChild className="text-3xl" />,
      title: "Youth Empowerment",
      description: "Providing digital skills training to young people to help them secure better opportunities."
    },
    {
      icon: <FaLightbulb className="text-3xl" />,
      title: "Innovative Programs",
      description: "Developing creative educational programs that adapt to the unique challenges faced by Ethiopian communities."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhotoIndex((prev) => (prev + 1) % slideshowImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Slideshow Section */}
      <section className="relative overflow-hidden h-screen">
        {slideshowImages.map((photo, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentPhotoIndex ? 1 : 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
            style={{ opacity: index === currentPhotoIndex ? 1 : 0 }}
          >
            <img
              src={photo.image}
              alt={photo.caption}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30" />
          </motion.div>
        ))}

        <div className="relative container mx-auto px-6 py-24 lg:py-32 h-full flex items-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl text-white"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <FaHandHoldingHeart className="text-6xl" />
            </motion.div>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-5xl lg:text-7xl font-bold mb-4 leading-tight"
            >
              100% Free Education for All Ethiopians
            </motion.h1>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl lg:text-2xl mb-2"
            >
              LXD Ethiopia NGO
            </motion.p>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-lg mb-8 max-w-2xl"
            >
              We provide completely free, accessible education to underserved communities across Ethiopia. No fees, no barriers - just opportunity for everyone.
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/onboarding"
                className="bg-white text-green-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl flex items-center gap-2"
              >
                <FaPlay /> Start Learning Free
              </Link>
              <Link
                to="/about"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all flex items-center gap-2"
              >
                Learn More <FaArrowRight />
              </Link>
            </motion.div>
          </motion.div>

          {/* Photo Caption */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-center"
          >
            <h3 className="text-2xl font-bold mb-2">{slideshowImages[currentPhotoIndex].caption}</h3>
            <p className="text-white/90 flex items-center justify-center gap-2">
              <FaGlobeAfrica /> {slideshowImages[currentPhotoIndex].location}
            </p>
          </motion.div>

          {/* Slideshow Indicators */}
          <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex gap-3">
            {slideshowImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPhotoIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentPhotoIndex ? 'bg-white w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Free Courses Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Free Courses Available
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              All our courses are 100% free. Start learning today at no cost
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {freeCourses.map((course, index) => (
              <motion.div
                key={course.id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm">
                    {course.category}
                  </div>
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-2 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <FaStar className="text-yellow-500" />
                    {course.rating}
                  </div>
                  <div className="absolute bottom-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    FREE
                  </div>
                </div>
                
                <div className="p-5">
                  <h3 className="font-bold text-lg text-gray-800 mb-2 line-clamp-2">
                    {course.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {course.level}
                    </span>
                    <span>•</span>
                    <span>{course.duration}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                    <FaUsers className="text-green-600" />
                    <span>{course.students.toLocaleString()} students</span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <div className="text-sm text-gray-500">Instructor</div>
                      <div className="text-sm font-semibold text-gray-700">{course.instructor}</div>
                    </div>
                  </div>
                  
                  <Link
                    to={`/courses/${course.id}`}
                    className="mt-4 w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                  >
                    Start Learning Free <FaArrowRight />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/courses"
              className="inline-block bg-green-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-700 transition-all transform hover:scale-105 shadow-xl"
            >
              View All Free Courses
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-blue-50 hover:shadow-xl transition-all"
              >
                <div className="text-purple-600 mb-3 flex justify-center text-3xl">
                  {stat.icon}
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Impact Stories Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Our Impact Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See how we're transforming lives and communities across Ethiopia through education
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {impactStories.map((story, index) => (
              <motion.div
                key={story.id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm">
                    {story.category}
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <FaHeart className="text-red-500" />
                    {story.beneficiaries} Beneficiaries
                  </div>
                </div>
                
                <div className="p-5">
                  <h3 className="font-bold text-lg text-gray-800 mb-2 line-clamp-2">
                    {story.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <FaGlobeAfrica className="text-green-600" />
                    <span>{story.location}</span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {story.story}
                  </p>
                  
                  <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2">
                    Read More <FaArrowRight />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/about"
              className="inline-block bg-green-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-700 transition-all transform hover:scale-105 shadow-xl"
            >
              View All Stories
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-green-700 via-emerald-600 to-teal-500 text-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              How We Help
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Our programs are designed to create lasting change in Ethiopian communities
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 hover:bg-white/20 transition-all"
              >
                <div className="text-yellow-300 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-white/90">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-12 lg:p-16 text-center text-white relative overflow-hidden"
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"
            />
            
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 relative z-10">
              Start Learning Today - 100% Free
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto relative z-10">
              All our courses, lessons, and resources are completely free. No hidden fees, no payments required. Education should be accessible to everyone.
            </p>
            <div className="flex flex-wrap gap-4 justify-center relative z-10">
              <Link
                to="/onboarding"
                className="bg-white text-green-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl flex items-center gap-2"
              >
                <FaPlay /> Start Learning Free
              </Link>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all flex items-center gap-2">
                <FaDonate /> Support Our Mission
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all flex items-center gap-2">
                <FaHandshake /> Volunteer
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LXDHome;
